import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../../services/auth.service';
import * as AuthActions from '../../store/actions/auth.actions';
import * as fromApp from '../../store/app.reducer';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private refreshingInProgress = false;
  private accessTokenSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>(null);

  constructor(
    private actionSubject: ActionsSubject,
    private authService: AuthService,
    private store: Store<fromApp.AppState>,
    private injector: Injector
  ) { }
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const user = this.authService.user;

    return next
      .handle(this.addAuthorizationHeaderOnHttpRequest(req, user?.access_token))
      .pipe(
        //
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 426) {
            return throwError(err);
          }
          //* JWT access_token Expired
          if (err instanceof HttpErrorResponse && err.status === 401) {
            if (user && user.refresh_token && user.access_token) {
              //* Refresh Token
              return this.refreshToken(req, next);
            }
            this.logoutAndRedirect(err);
          }

          if (
            err instanceof HttpErrorResponse &&
            err.status === 403 &&
            req.url.includes('refresh-token')
          ) {
            return this.logoutAndRedirect(err);
          }

          return throwError(err);
        })
      );
  }

  private addAuthorizationHeaderOnHttpRequest(
    req: HttpRequest<any>,
    access_token: string
  ): HttpRequest<any> {
    if (access_token) {
      return req.clone({
        setHeaders: { Authorization: `Bearer ${access_token}` },
      });
    }

    return req;
  }

  private logoutAndRedirect(
    err: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    this.refreshingInProgress = false;
    this.authService.logoutUser();
    return throwError(err);
  }

  private refreshToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.refreshingInProgress) {
      this.refreshingInProgress = true;
      this.accessTokenSubject.next(null);

      const user = this.authService.user;
      this.store.dispatch(
        AuthActions.refreshToken({
          payload: { user_id: user.id, refresh_token: user.refresh_token },
        })
      );
      return this.actionSubject.pipe(
        ofType(AuthActions.tokenRefreshed),
        take(1),
        switchMap((action) => {
          this.accessTokenSubject.next(action.payload.access_token);
          this.refreshingInProgress = false;

          return next.handle(
            this.addAuthorizationHeaderOnHttpRequest(
              req,
              action.payload.access_token
            )
          );
        })
      );
    } else {
      return this.accessTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) => {
          return next.handle(
            this.addAuthorizationHeaderOnHttpRequest(req, token)
          );
        })
      );
    }
  }
}
